<template>
  <v-autocomplete
    label="Source Platform"
    v-model="localSelected"
    :search-input.sync="searchText"
    :items="items"
    item-text="displayText"
    chips
    small-chips
    :clearable="true"
    deletable-chips
    hide-details
    multiple
    class="mb-4"
    @change="handleOnChange()"
  >
    <template v-slot:prepend-item>
      <!-- Select All -->
      <v-list-item
        ripple
        @mousedown.prevent
        @click="selectAllItems"
        class="unindented"
      >
        <v-list-item-action>
          <v-icon :color="localSelected.length > 0 ? 'primary' : ''">
            {{ itemsAllIcon }}
          </v-icon>
        </v-list-item-action>
        <v-list-item-content>
          <v-list-item-title>
            Select All
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <v-divider class="mt-2"></v-divider>

      <!-- News -->
      <v-list-item
        ripple
        @mousedown.prevent
        @click="toggleNews"
        class="unindented"
        v-show="!searchText || 'news'.indexOf(searchText.toLowerCase()) > -1"
      >
        <v-list-item-action>
          <v-icon :color="newsActive ? 'primary' : ''">
            {{ newsIcon }}
          </v-icon>
        </v-list-item-action>
        <v-list-item-content>
          <v-list-item-title>
            News
            <span v-if="counts && !isNaN(counts.news)"
              >({{ counts.news }})</span
            >
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <!-- Select All Social -->
      <v-list-item
        ripple
        @mousedown.prevent
        @click="toggleAllSocial"
        class="unindented"
      >
        <v-list-item-action>
          <v-icon :color="someSocialActive ? 'primary' : ''">
            {{ socialIcon }}
          </v-icon>
        </v-list-item-action>
        <v-list-item-content>
          <v-list-item-title>
            Social
            <span v-if="counts && !isNaN(counts.social)"
              >({{ counts.social }})</span
            >
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </template>

    <!-- Using item slot to hide news inside social list without omitting it from  -->
    <template v-slot:item="{ item, attrs }">
      <v-list-item
        v-show="item.value !== NEWS_VALUE"
        @click="togglePlatform(item.value)"
      >
        <v-list-item-action>
          <v-icon :color="attrs.inputValue ? 'primary' : ''">
            {{ attrs.inputValue ? TOTAL_CHECK_ICON : EMPTY_CHECK_ICON }}
          </v-icon>
        </v-list-item-action>
        <v-list-item-content>
          <v-list-item-title>
            {{ item.displayText }}
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </template>
  </v-autocomplete>
</template>

<script>
import lodashCloneDeep from "lodash/cloneDeep";

import { SOURCE_METADATA_PLATFORM } from "@/common/constants/search";

const EMITTED_ACTIONS = {
  CHANGE: "change",
};

const TOTAL_CHECK_ICON = "mdi-checkbox-marked";
const PARTIAL_CHECK_ICON = "mdi-minus-box";
const EMPTY_CHECK_ICON = "mdi-checkbox-blank-outline";
const NEWS_VALUE = "news";

export default {
  name: "SearchFilterSourcePlatform",
  props: {
    selected: {
      type: Array,
    },
    counts: {
      type: Object,
    },
    reset: {
      type: String,
    },
  },
  data() {
    return {
      localSelected: [],
      items: [],
      searchText: "",
    };
  },
  created() {
    //constants used in template
    this.EMPTY_CHECK_ICON = EMPTY_CHECK_ICON;
    this.TOTAL_CHECK_ICON = TOTAL_CHECK_ICON;
    this.NEWS_VALUE = NEWS_VALUE;
  },
  computed: {
    itemsAllIcon() {
      if (this.localSelected.length === this.items.length) {
        return TOTAL_CHECK_ICON;
      }
      if (
        this.localSelected.length > 0 &&
        this.localSelected.length < this.items.length
      ) {
        return PARTIAL_CHECK_ICON;
      }
      return EMPTY_CHECK_ICON;
    },
    socialIcon() {
      let numSelectedSocial = this.getSelectedSocial().length;
      let numSocialPlatforms = this.getSocialPlatforms().length;
      let numUnselectedSocial = numSocialPlatforms - numSelectedSocial;
      if (numUnselectedSocial === 0) {
        return TOTAL_CHECK_ICON;
      }
      if (numSelectedSocial > 0) {
        return PARTIAL_CHECK_ICON;
      }
      return EMPTY_CHECK_ICON;
    },
    newsIcon() {
      if (this.newsActive) {
        return TOTAL_CHECK_ICON;
      }
      return EMPTY_CHECK_ICON;
    },
    newsActive() {
      return this.localSelected.includes(NEWS_VALUE);
    },
    someSocialActive() {
      let numSelectedSocial = this.getSelectedSocial().length;
      return numSelectedSocial > 0;
    },
  },
  methods: {
    setupItems() {
      const arr = lodashCloneDeep(SOURCE_METADATA_PLATFORM);
      if (this.counts) {
        arr.forEach((item) => {
          Object.keys(this.counts).forEach((key) => {
            if (key === item.value) {
              item.count = this.counts[key];
              item.displayText = `${item.text} (${item.count})`;
            }
          });
        });
      }
      this.items = arr;
    },
    handleOnChange() {
      this.$emit(EMITTED_ACTIONS.CHANGE, [
        {
          type: "platform",
          value: this.localSelected,
        },
      ]);
    },
    selectAllItems() {
      this.$nextTick(() => {
        if (this.localSelected.length >= this.items.length) {
          this.localSelected = [];
        } else {
          this.localSelected = this.items.map((item) => item.value);
        }
        this.handleOnChange();
      });
    },
    toggleAllSocial() {
      let numSelectedSocial = this.getSelectedSocial().length;
      let numSocialPlatforms = this.getSocialPlatforms().length;
      let numUnselectedSocial = numSocialPlatforms - numSelectedSocial;
      let selectionToUse = this.newsActive ? [NEWS_VALUE] : [];
      if (numUnselectedSocial > 0) {
        selectionToUse = [
          ...selectionToUse,
          ...this.getSocialPlatforms().map((item) => item.value),
        ];
      }
      this.localSelected = selectionToUse;
      this.handleOnChange();
    },
    toggleNews() {
      if (!this.localSelected.includes(NEWS_VALUE)) {
        this.localSelected.push(NEWS_VALUE);
      } else {
        this.localSelected = this.localSelected.filter((item) => {
          return item !== NEWS_VALUE;
        });
      }
      this.handleOnChange();
    },
    togglePlatform(platformToToggle) {
      if (!this.localSelected.includes(platformToToggle)) {
        this.localSelected.push(platformToToggle);
      } else {
        this.localSelected = this.localSelected.filter((item) => {
          return item !== platformToToggle;
        });
      }
      this.handleOnChange();
    },
    getSelectedSocial() {
      return this.localSelected.filter((item) => {
        return item !== NEWS_VALUE;
      });
    },
    getSocialPlatforms() {
      return this.items.filter((item) => {
        return item.value !== NEWS_VALUE;
      });
    },
    convertSourcesArrayToObject(sources) {
      let res = {};
      for (const source of SOURCE_METADATA_PLATFORM) {
        res[source.value] = sources.includes(source.value);
      }
      return res;
    },
    convertSourcesObjectToArray(sources) {
      let res = [];
      for (let [source, value] of Object.entries(sources)) {
        if (sources.hasOwnProperty(source) && value) {
          res.push(source);
        }
      }
      return res;
    },
  },
  watch: {
    selected: {
      handler() {
        this.setupItems();
        this.localSelected = this.selected ?? [];
      },
      deep: true,
      immediate: true,
    },
    counts: {
      handler() {
        this.setupItems();
      },
      deep: true,
    },
    reset: {
      handler() {
        this.localSelected = [];
      },
    },
  },
};
</script>

<style>
.search-box.theme--light.v-text-field--solo-inverted.v-input--is-focused
  > .v-input__control
  > .v-input__slot
  input {
  color: rgba(0, 0, 0, 0.6);
}
:not(.unindented).v-list-item {
  padding-left: 30px;
}
</style>
